import React from 'react';
import { StyleSheet, Text, View, ViewStyle, Image } from 'react-native';

import { Charge, TransactionItem } from 'apollo/queries/autogenerate/schemas';
import IllustrationSupport from 'shared/assets/images/illustration-support@3x.png';
import GradientProgress from 'shared/modules/common/components/molecules/GradientProgress';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import {
  getCurrentInstallmentIndex,
  getTotalInstallment,
} from 'shared/modules/usage/utils/charge-utils';
import { formatToCurrency } from 'shared/utils/japanese';

const InstallmentProgress = (props: {
  installmentNumber: number;
  numberOfInstallments: number;
}) => (
  <View style={styles.installmentProgress}>
    <Text style={styles.installmentProgressHeaderText}>現在のお支払い状況</Text>
    <GradientProgress
      progress={props.installmentNumber / props.numberOfInstallments}
    />
    <Text style={styles.installmentProgressText}>
      {props.installmentNumber} / {props.numberOfInstallments}回
    </Text>
  </View>
);

const DetailItem = (props: {
  label: string;
  value?: string;
  note?: string;
  style?: ViewStyle;
}) => (
  <View style={[styles.detailItem, props.style]}>
    <View style={styles.detailItemLabelValueWrapper}>
      <Text style={styles.detailItemLabelText}>{props.label}</Text>
      {props.value && (
        <Text style={styles.detailItemValueText}>{props.value}</Text>
      )}
    </View>
    {props.note && <Text style={styles.detailNoteText}>{props.note}</Text>}
  </View>
);

const Information = () => {
  return (
    <View style={styles.information}>
      <View style={styles.informationImageWrapper}>
        <Image source={IllustrationSupport} style={styles.informationImage} />
      </View>
      <Text style={styles.informationText}>
        購入時に選択された通信キャリア割引につきまして、Appleの条件を満たさなかったため、割引が適用されませんでした。
        {'\n\n'}
        購入時にお知らせした通り、お客様のペイディアカウントに差額をご請求させていただきます。
        {'\n\n'}
        通信キャリア割引に関するご質問やご不明な点は、Appleのカスタマーサポート0120-993-993までお電話でお問い合わせください。
      </Text>
    </View>
  );
};

type Props = {
  charge: Charge;
  transactionItem: TransactionItem;
};

const AppleCarrierDiscountDetails = (props: Props) => {
  const charge = props.charge;
  const item = props.transactionItem;

  const carrierDiscountAmount = item.unitPrice;
  const carrierDiscountAmountAbove3000 = carrierDiscountAmount > 3000;
  const installmentAmount = item?.monthlyAmount ?? 0;
  const installmentNumber = getCurrentInstallmentIndex(charge);
  const numberOfInstallments = getTotalInstallment(charge);

  if (!numberOfInstallments || installmentNumber === -1) {
    return null;
  }

  return (
    <View style={styles.contentView}>
      {/** progress bar */}
      {carrierDiscountAmountAbove3000 && (
        <InstallmentProgress
          installmentNumber={installmentNumber}
          numberOfInstallments={numberOfInstallments}
        />
      )}

      {/** summary. */}
      <DetailItem
        label="通信キャリア割引調整額"
        value={
          carrierDiscountAmount && carrierDiscountAmount < 3000
            ? `${formatToCurrency(carrierDiscountAmount)}`
            : undefined
        }
        style={styles.summaryDetailItem}
      />

      {/** detail. */}
      {carrierDiscountAmountAbove3000 && (
        <>
          {/** Trade in installment amount */}
          <DetailItem
            label="分割支払い金"
            value={`${formatToCurrency(
              installmentAmount
            )} / ${numberOfInstallments}月`}
          />
          {/** interest rate */}
          <DetailItem
            label="分割金利*"
            value={`0% / ${numberOfInstallments}ヶ月`}
            note="*口座振替、銀行振り込みに限ります"
          />
          {/** total */}
          <View style={styles.totalItem}>
            <Text style={styles.totalLabelText}>合計</Text>
            <Text style={styles.totalValueText}>
              {formatToCurrency(carrierDiscountAmount)}
            </Text>
          </View>
        </>
      )}

      {/** carrier discount banner */}
      <Information />
    </View>
  );
};

const styles = StyleSheet.create({
  contentView: {
    backgroundColor: colors.white,
    flexGrow: 1,
    padding: 32,
  },
  installmentProgress: {
    marginBottom: 24,
  },
  installmentProgressHeaderText: {
    ...typography.title05,
    color: colors.blackMedium,
    marginBottom: 24,
  },
  installmentProgressText: {
    ...typography.bodyBold,
    marginTop: 12,
  },
  detailItem: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.greyLight,
  },
  detailItemLabelValueWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  detailItemLabelText: {
    ...typography.caption01,
  },
  detailItemValueText: {
    ...typography.caption02,
    color: colors.teal,
  },
  detailNoteText: {
    ...typography.caption02,
    marginTop: 16,
  },
  summaryDetailItem: {
    paddingVertical: 0,
    borderBottomWidth: 0,
  },
  totalItem: {
    paddingTop: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  totalLabelText: {
    ...typography.bodyBold,
  },
  totalValueText: {
    ...typography.bodyBold,
  },
  information: {
    backgroundColor: colors.greySuperLight,
    borderRadius: 16,
    padding: 32,
    marginTop: 24,
  },
  informationImageWrapper: {
    marginBottom: 24,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  informationImage: {
    width: 88,
    height: 56,
  },
  informationText: {
    ...typography.caption02,
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 16,
    textAlign: 'center',
    color: colors.blackMedium,
  },
});

export default AppleCarrierDiscountDetails;
